/**
 * The session record: one session's replayable event stream.
 *
 * A session is one sitting on one surface. Everything in it is timed on one session clock:
 * milliseconds since the session started, at full precision. It holds the realized scores its
 * takes graded against (materials), its takes (each with the setup it was graded under, its
 * stamp and its result) and the stream itself, cut into sealed segments: every key press,
 * release and pedal change, plus tempo, cursor and settings events. A take is the graded atom;
 * events between takes are recorded too.
 *
 * Every segment carries one off-the-record value for all of its events: a segment is cut at
 * every flip, at every take boundary, and after a bounded number of events.
 */

import { CapabilityMask, JudgmentConfig } from '../grader';
import { ScoringConfig } from '../preset';
import { Surface } from '../presetData';
import { Dimension, PerformanceEvent, RealizedScore } from '../types';
import { ClockSpec } from './clockSpec';
import { AssistName, TakeStamp } from './stamp';
import { TakeResult } from './takeResult';

export const STREAM_FORMAT_VERSION = 1;

/** A segment is sealed after this many events, so a teardown flush stays small. */
export const MAX_SEGMENT_EVENTS = 500;

export interface SessionHeader {
  /** Generated on the device; the session's identity everywhere. */
  readonly sessionId: string;
  readonly surface: Surface;
  /**
   * The surface's own play-session row, when it has one (sight reading, repertoire and exercises
   * do; tutorials do not).
   */
  readonly surfaceSessionId: string | null;
  /** Wall-clock time at session clock 0 (ISO 8601). */
  readonly startedAt: string;
  readonly formatVersion: number;
  readonly appVersion: string | null;
  /** Off the record when the session started. */
  readonly offTheRecord: boolean;
}

/** A realized score some take grades against: notes after ornaments, transposition, arrangement. */
export interface Material {
  readonly materialId: string;
  /** Sight reading: the generated phrase the score was realized from. */
  readonly phraseId: number | null;
  readonly atMs: number;
  readonly score: RealizedScore;
}

export interface BeatRange {
  /** Grade the notes whose notated onset is at or after this beat ... */
  readonly fromBeat: number;
  /** ... and before this one. */
  readonly toBeat: number;
}

/** How a take's material was drawn. */
export type KeySignatureDisplay = 'show' | 'accidentals_only';

export const KEY_SIGNATURE_DISPLAYS: readonly KeySignatureDisplay[] = ['show', 'accidentals_only'];

/**
 * How the take's material was shown to the player: plain display facts. They are not assists
 * (they give nothing away that the take is graded on), the engine never reads them, and they never
 * enter the stamp, so they never decide what a take counts toward. They are recorded so their
 * effect can be measured later.
 */
export interface TakeDisplay {
  /**
   * Scales: written under their key signature (`show`), or with no key signature and every
   * accidental on its note (`accidentals_only`).
   */
  readonly keySignature?: KeySignatureDisplay;
}

/**
 * What the engine needs, besides the material and the hands played, to grade the take again and
 * score it, as data; plus, when the surface gives them, the take's display facts.
 */
export interface RecordedTakeSetup {
  /** A loop or passage: only part of the material is graded. */
  readonly beatRange: BeatRange | null;
  /** Layers 0 to 1.5: how played notes are matched and credited. */
  readonly judgment: JudgmentConfig;
  /**
   * Layers 2 and 3: how the take is combined into its grade and judged. Recorded so the take's
   * result (and any verdict) comes back from the record alone, under the rules it was played
   * under, even after a preset's definition changes.
   */
  readonly scoring: ScoringConfig;
  /** The clock when the take began; tempo events inside the take extend it. Null: untimed. */
  readonly clock: ClockSpec | null;
  readonly referenceTempoBpm: number | null;
  readonly mask: CapabilityMask;
  readonly narrowedAway: readonly Dimension[];
  /** How the material was shown; left out when the surface gives none. Never read by the engine. */
  readonly display?: TakeDisplay;
}

export type TempoSource = 'player' | 'auto' | 'setting';

export interface TempoEvent {
  readonly kind: 'tempo';
  readonly timeMs: number;
  readonly bpm: number;
  readonly source: TempoSource;
}

/**
 * The playhead jumped (a seek, a loop wrapping, a hold). Between jumps the cursor follows the
 * take's clock, so continuous motion is not recorded.
 */
export interface CursorEvent {
  readonly kind: 'cursor';
  readonly timeMs: number;
  readonly beat: number;
  readonly cause: 'start' | 'seek' | 'loop' | 'hold';
}

export type SettingChange =
  /** An assist's level, 0 (off) to 1. */
  | { readonly name: 'assist'; readonly assist: AssistName; readonly level: number }
  | { readonly name: 'off-the-record'; readonly on: boolean };

export interface SettingEvent {
  readonly kind: 'setting';
  readonly timeMs: number;
  readonly change: SettingChange;
}

/** What a segment holds: the engine's performance events and the stream's own events. */
export type StreamEvent = PerformanceEvent | TempoEvent | CursorEvent | SettingEvent;

export interface Segment {
  /** Position in the session, from 0. */
  readonly seq: number;
  /** The take the events belong to; null between takes. */
  readonly takeId: string | null;
  readonly offTheRecord: boolean;
  readonly events: readonly StreamEvent[];
}

export type TakeEndReason =
  | 'finished'
  | 'stopped'
  | 'restarted'
  | 'loop-wrapped'
  | 'skipped'
  | 'left';

export interface TakeEnd {
  readonly endMs: number;
  readonly reason: TakeEndReason;
  readonly stamp: TakeStamp;
  readonly result: TakeResult;
  readonly segmentCount: number;
  readonly eventCount: number;
}

export interface RecordedTake {
  readonly takeId: string;
  readonly takeIndex: number;
  readonly startMs: number;
  /** The seq the take's first segment has (or would have, for a take with no events). */
  readonly firstSeq: number;
  readonly materialId: string;
  readonly setup: RecordedTakeSetup;
  /** The stamp as the take began (it carries the hands played); the end carries the final one. */
  readonly stampAtStart: TakeStamp;
  /** Null while the take is open, and for a take that was never ended (abandoned). */
  readonly end: TakeEnd | null;
}

/** What is stored for a session: the replayable record. */
export interface SessionRecord {
  readonly header: SessionHeader;
  readonly materials: readonly Material[];
  readonly takes: readonly RecordedTake[];
  /** Sealed segments, by seq. */
  readonly segments: readonly Segment[];
}
